import { z } from 'zod';

import { RagDocumentSchema } from '../shared/schemas/RagDocumentSchema.js';

export const OwnerSchema = z.object({
  userUuid: z.string(),
  userId: z.number().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
});

export const WorkspaceUserSchema = z.object({
  userId: z.number(),
  email: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
});

export const GrantedUserSchema = WorkspaceUserSchema.extend({
  userId: z.number().nullish(),
});

export const FolderSchema = z.object({
  folderId: z.string(),
  clientId: z.number().nullish(),
  name: z.string(),
  description: z.string().nullish(),
  parentFolderId: z.string().nullish(),
  /** Creator uuid; null for system buckets such as Workspace Files. */
  createdBy: z.string().nullish(),
  depth: z.number().nullish(),
  isBucket: z.boolean().nullish(),
  isPrivate: z.boolean().nullish(),
  allWorkspace: z.boolean().nullish(),
  isSystem: z.boolean().nullish(),
  hasChildren: z.boolean().nullish(),
  grantedUsers: z.array(GrantedUserSchema).nullish(),
  owner: OwnerSchema.nullish(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
});

/**
 * The documents endpoint's file plus where it lives. Files carry no grant of
 * their own: the folder holding them decides access.
 */
export const KnowledgeBaseFileSchema = RagDocumentSchema.extend({
  folderId: z.string().nullish(),
  extension: z.string().nullish(),
  sizeBytes: z.number().nullish(),
});

export const BreadcrumbSchema = z.object({
  folderId: z.string().nullish(),
  name: z.string(),
});

export const KnowledgeBaseListingSchema = z.object({
  breadcrumb: z.array(BreadcrumbSchema).nullish(),
  folders: z.array(FolderSchema).nullish(),
  files: z.array(KnowledgeBaseFileSchema).nullish(),
});

export const StorageSchema = z.object({
  usedBytes: z.number(),
  limitBytes: z.number(),
  usedPercent: z.number(),
});

/**
 * Without `parentFolderId` this creates a bucket, `isPrivate` picking which
 * kind; inside a bucket it creates a plain folder.
 */
export const CreateFolderRequestSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().nullish(),
  parentFolderId: z.string().nullish(),
  isPrivate: z.boolean().nullish(),
});

export const UpdateFolderRequestSchema = z.object({
  name: z.string().min(1).max(200),
});

/**
 * Neither key is nullable: a row at depth 0 is a bucket, and buckets are
 * created, not moved into.
 */
export const MoveFolderRequestSchema = z.object({
  newParentFolderId: z.string(),
});

export const MoveFileRequestSchema = z.object({
  newFolderId: z.string(),
});

export const FolderGrantSchema = z.object({
  granteeType: z.enum(['user', 'tag', 'role']).nullish(),
  granteeId: z.string(),
});

export const SetFolderGrantsRequestSchema = z.object({
  grants: z.array(FolderGrantSchema).nullish(),
  allWorkspace: z.boolean().nullish(),
});
